import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  styled,
  alpha,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { Profile, COLLECTIONS } from '../../types/firebase';
import MainLayout from '../Layout/MainLayout';

// Styled components - use theme glass; only layout
const ProfileCard = styled(Card)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  padding: '20px',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '16px',
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
  },
}));

// Use theme Button (contained) - no custom styled override

const CreateCompanyProfile: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state based on mobile app structure
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    address: '',
    website: '',
    firstName: '',
    lastName: '',
    position: '',
  });

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateForm = (): boolean => {
    // Validation matching mobile app requirements
    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!formData.industry.trim()) {
      setError('Industry is required');
      return false;
    }
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.position.trim()) {
      setError('Position is required');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm() || !userProfile?.masterOwnerID) return;

    setLoading(true);
    setError(null);

    try {
      // Create full name for createdBy field
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      
      // Create profile data matching mobile app structure
      const profileData: Omit<Profile, 'id'> = {
        companyName: formData.companyName,
        industry: formData.industry,
        address: formData.address,
        website: formData.website,
        createdBy: fullName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.position, // Map position to title
        email: userProfile.email,
        masterOwnerID: userProfile.masterOwnerID,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Save to Firebase using masterOwnerID as document ID
      const profileRef = doc(db, COLLECTIONS.COMPANIES, userProfile.masterOwnerID);
      await setDoc(profileRef, profileData);

      setSuccess('Company profile created successfully!');
      
      // Redirect to profile page after a short delay
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
      
    } catch (err) {
      console.error('Error creating profile:', err);
      setError('Failed to create company profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title="Create Company Profile">
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 200px)">
        <ProfileCard>
          <CardContent>
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
                Create Company Profile
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Set up your company profile to get started with SkimBuster
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Company Information */}
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="text.primary" mb={2}>
                    Company Information
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                  <StyledTextField
                    fullWidth
                    required
                    label="Company Name"
                    value={formData.companyName}
                    onChange={handleInputChange('companyName')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledTextField
                    fullWidth
                    required
                    label="Industry"
                    value={formData.industry}
                    onChange={handleInputChange('industry')}
                    placeholder="e.g., Finance, Retail, Healthcare"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box>
                  <StyledTextField
                    fullWidth
                    label="Company Address"
                    value={formData.address}
                    onChange={handleInputChange('address')}
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box>
                  <StyledTextField
                    fullWidth
                    label="Company Website"
                    value={formData.website}
                    onChange={handleInputChange('website')}
                    placeholder="https://www.example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LanguageIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Personal Information */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" fontWeight="bold" color="text.primary" mb={2}>
                    Your Information
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                  <StyledTextField
                    fullWidth
                    required
                    label="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledTextField
                    fullWidth
                    required
                    label="Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box>
                  <StyledTextField
                    fullWidth
                    required
                    label="Your Position"
                    value={formData.position}
                    onChange={handleInputChange('position')}
                    placeholder="e.g., CEO, Manager, Security Officer"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Submit Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    size="large"
                  >
                    {loading ? 'Creating Profile...' : 'Create Profile'}
                  </Button>
                </Box>
              </Box>
            </form>
          </CardContent>
        </ProfileCard>
      </Box>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default CreateCompanyProfile;